import { useMemo } from 'react';
import { FiservCardStatusEnum } from '@common-fe/common-fe';

import { DependentStatusEnum } from '@/modules/employee/employee.types';
import { useDependentsList } from '@/modules/employee/Employee/Dependents/hooks';
import { ROOT_DEPENDENT_ID } from '@/modules/transaction/Cards/hooks/useCardFormatting';
import { useGetCardsQuery } from '@/modules/transaction/Cards/queries/useGetCards.query';

interface Props {
  employeeId?: string;
}

const EXCLUDED_CARD_STATUSES = [
  FiservCardStatusEnum.CLOSED,
  FiservCardStatusEnum.PENDING_CLOSED,
  FiservCardStatusEnum.LOST,
  FiservCardStatusEnum.STOLEN,
  FiservCardStatusEnum.NOT_SENT,
  FiservCardStatusEnum.PENDING_REPLACE,
];

export const useFilteredDependents = ({ employeeId }: Props) => {
  const { formattedDependents, isLoading: dependentsLoading } = useDependentsList({ 
    employeeId,
    selectedStatuses: [DependentStatusEnum.ACTIVE]
  });
  
  const { cardMap, isLoading: cardHoldersLoading } = useGetCardsQuery(employeeId);

  const filteredDependents = useMemo(() => {
    if (!formattedDependents.length) return [];
    if (!cardMap || Object.keys(cardMap).length === 0) return formattedDependents;

    const dependentsWithActiveCards = new Set<string>();

    Object.values(cardMap).forEach(holderCards => {
      Object.entries(holderCards).forEach(([status, cards]) => {
        if (!EXCLUDED_CARD_STATUSES.includes(status as FiservCardStatusEnum) && cards.length > 0) {
          cards.forEach(card => {
            if (card.holder.dependentId && card.holder.dependentId !== ROOT_DEPENDENT_ID) {
              dependentsWithActiveCards.add(card.holder.dependentId);
            }
          });
        }
      });
    });

    return formattedDependents.filter(dependent => 
      dependent.dependentId && !dependentsWithActiveCards.has(dependent.dependentId)
    );
  }, [formattedDependents, cardMap]);

  return {
    filteredDependents,
    isLoading: dependentsLoading || cardHoldersLoading,
  };
};